import { useCallback, useEffect, useRef, useState } from "react";

import styles from "./PlayerControls.module.scss";

const { controls, compact, button, progress, time } = styles;

/** 이 폭 이하에서는 아이콘/여백을 줄여서 모든 버튼이 보이도록 한다. */
const COMPACT_WIDTH = 220;
/** 이 폭 이상에서만 재생시간 텍스트를 노출한다. */
const TIME_WIDTH = 300;

interface PlayerControlsProps {
  video: HTMLVideoElement | null;
  containerRef: React.RefObject<HTMLElement>;
  /** BP-5248: 전체화면 버튼 노출 여부 (기본 true) */
  showFullscreen?: boolean;
  /** BP-5248: 음소거 버튼 노출 여부 (기본 true) */
  showMute?: boolean;
}

function formatTime(seconds: number) {
  if (!isFinite(seconds) || seconds < 0) return "0:00";

  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;

  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

function isFullscreen() {
  return Boolean(
    document.fullscreenElement ??
      (document as any).webkitFullscreenElement ??
      (document as any).webkitCurrentFullScreenElement
  );
}

/**
 * BP-5213: 브라우저 기본 컨트롤러는 video 폭이 좁아지면 음소거/전체화면 버튼을
 * 임의로 숨긴다. 임베디드 플레이어는 작은 iframe 으로 삽입되는 경우가 많아
 * 해상도와 무관하게 모든 버튼이 유지되도록 컨트롤러를 직접 구현한다.
 */
export default function PlayerControls({
  video,
  containerRef,
  showFullscreen = true,
  showMute = true,
}: PlayerControlsProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [width, setWidth] = useState(0);
  const seekingRef = useRef(false);

  /* video 상태 동기화 */
  useEffect(() => {
    if (!video) return;

    const syncPlay = () => setPlaying(!video.paused);
    const syncMuted = () => setMuted(video.muted);
    const syncDuration = () =>
      setDuration(isFinite(video.duration) ? video.duration : 0);
    const syncTime = () => {
      if (seekingRef.current) return;
      setCurrentTime(video.currentTime);
    };

    syncPlay();
    syncMuted();
    syncDuration();
    syncTime();

    video.addEventListener("play", syncPlay);
    video.addEventListener("pause", syncPlay);
    video.addEventListener("ended", syncPlay);
    video.addEventListener("volumechange", syncMuted);
    video.addEventListener("loadedmetadata", syncDuration);
    video.addEventListener("durationchange", syncDuration);
    video.addEventListener("timeupdate", syncTime);
    video.addEventListener("seeked", syncTime);

    return () => {
      video.removeEventListener("play", syncPlay);
      video.removeEventListener("pause", syncPlay);
      video.removeEventListener("ended", syncPlay);
      video.removeEventListener("volumechange", syncMuted);
      video.removeEventListener("loadedmetadata", syncDuration);
      video.removeEventListener("durationchange", syncDuration);
      video.removeEventListener("timeupdate", syncTime);
      video.removeEventListener("seeked", syncTime);
    };
  }, [video]);

  /* 전체화면 상태 동기화 (iOS 는 video 전용 이벤트를 사용한다) */
  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen());
    const enter = () => setFullscreen(true);
    const exit = () => setFullscreen(false);

    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    video?.addEventListener("webkitbeginfullscreen", enter);
    video?.addEventListener("webkitendfullscreen", exit);

    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
      video?.removeEventListener("webkitbeginfullscreen", enter);
      video?.removeEventListener("webkitendfullscreen", exit);
    };
  }, [video]);

  /* 컨테이너 폭에 따라 compact 여부 결정 */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setWidth(container.clientWidth);

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef]);

  const handleClickPlay = useCallback(() => {
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  }, [video]);

  const handleClickMute = useCallback(() => {
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, [video]);

  const handleClickFullscreen = useCallback(() => {
    const container = containerRef.current;

    if (isFullscreen()) {
      if (document.exitFullscreen) document.exitFullscreen();
      else (document as any).webkitExitFullscreen?.();
      return;
    }

    /* iOS Safari 는 container 전체화면을 지원하지 않아 video 로 대체한다. */
    const enterVideoFullscreen = () =>
      (video as any)?.webkitEnterFullscreen?.();

    if (container?.requestFullscreen) {
      container.requestFullscreen().catch(enterVideoFullscreen);
    } else if ((container as any)?.webkitRequestFullscreen) {
      (container as any).webkitRequestFullscreen();
    } else {
      enterVideoFullscreen();
    }
  }, [containerRef, video]);

  const handleChangeProgress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value);
    setCurrentTime(next);
    if (video) video.currentTime = next;
  };

  if (!video) return null;

  const isCompact = width > 0 && width < COMPACT_WIDTH;
  const showTime = width >= TIME_WIDTH;
  const played = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`${controls} ${isCompact ? compact : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={button}
        aria-label={playing ? "일시정지" : "재생"}
        onClick={handleClickPlay}
      >
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      {showMute && (
        <button
          type="button"
          className={button}
          aria-label={muted ? "음소거 해제" : "음소거"}
          onClick={handleClickMute}
        >
          {muted ? <IconMuted /> : <IconVolume />}
        </button>
      )}
      <input
        type="range"
        className={progress}
        aria-label="재생 위치"
        min={0}
        max={duration || 0}
        step={0.1}
        value={Math.min(currentTime, duration || 0)}
        disabled={!duration}
        style={{
          backgroundSize: `${played}% 100%`
        }}
        onPointerDown={() => {
          seekingRef.current = true;
        }}
        onPointerUp={() => {
          seekingRef.current = false;
        }}
        onKeyDown={() => {
          seekingRef.current = true;
        }}
        onKeyUp={() => {
          seekingRef.current = false;
        }}
        onChange={handleChangeProgress}
      />
      {showTime && (
        <span className={time}>
          {`${formatTime(currentTime)} / ${formatTime(duration)}`}
        </span>
      )}
      {showFullscreen && (
        <button
          type="button"
          className={button}
          aria-label={fullscreen ? "전체화면 종료" : "전체화면"}
          onClick={handleClickFullscreen}
        >
          {fullscreen ? <IconFullscreenExit /> : <IconFullscreen />}
        </button>
      )}
    </div>
  );
}

function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5-11-6.5z" />
    </svg>
  );
}

function IconPause() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.5 5h3.5v14H6.5zM14 5h3.5v14H14z" />
    </svg>
  );
}

function IconVolume() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4v-5z" />
      <path d="M15.5 8.8a4.5 4.5 0 0 1 0 6.4l1.2 1.2a6.2 6.2 0 0 0 0-8.8l-1.2 1.2z" />
      <path d="M18.2 6.1a8.3 8.3 0 0 1 0 11.8l1.2 1.2a10 10 0 0 0 0-14.2l-1.2 1.2z" />
    </svg>
  );
}

function IconMuted() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4v-5z" />
      <path d="M15.3 9.3l1.4-1.4 5 5-1.4 1.4-5-5z" />
      <path d="M20.3 7.9l1.4 1.4-5 5-1.4-1.4 5-5z" />
    </svg>
  );
}

function IconFullscreen() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 4h7v2.2H6.2V11H4V4zm9 0h7v7h-2.2V6.2H13V4zM4 13h2.2v4.8H11V20H4v-7zm13.8 0H20v7h-7v-2.2h4.8V13z" />
    </svg>
  );
}

function IconFullscreenExit() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8.8 4H11v7H4V8.8h4.8V4zM13 4h2.2v4.8H20V11h-7V4zM4 13h7v7H8.8v-4.8H4V13zm9 0h7v2.2h-4.8V20H13v-7z" />
    </svg>
  );
}
